import { useEffect, useRef } from "react";

interface Props {
  /** The loader id, e.g. "326cc16641b2405ed5265209433" */
  loaderId: string;
  className?: string;
  /** Force immediate load (skip IntersectionObserver). Default false. */
  eager?: boolean;
}

/**
 * Lazy-loads a Trustindex widget only when it scrolls into view (or on first
 * user interaction for above-the-fold widgets). This keeps the 4 different
 * Trustindex loaders off the critical render path and out of PageSpeed's
 * "unused JavaScript" / "duplicate JavaScript" buckets on initial load.
 */
export function TrustindexWidget({ loaderId, className, eager = false }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let loaded = false;
    const load = () => {
      if (loaded || el.querySelector("script")) return;
      loaded = true;
      const script = document.createElement("script");
      script.src = `https://cdn.trustindex.io/loader.js?${loaderId}`;
      script.defer = true;
      script.async = true;
      el.appendChild(script);
    };

    if (eager) {
      // Defer just past first paint
      const t = setTimeout(load, 1500);
      return () => clearTimeout(t);
    }

    // Lazy: load when widget approaches the viewport
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            load();
            io.disconnect();
          }
        },
        { rootMargin: "300px" }
      );
      io.observe(el);
      return () => io.disconnect();
    }

    // Fallback
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [loaderId, eager]);

  return <div ref={ref} className={className} />;
}
